import { ContainerErrorType } from '@fluidframework/container-definitions';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { OdspErrorType } from '@fluidframework/odsp-driver-definitions';

export type OfficeFluidErrorType =
  | OWHErrorType
  | ContainerErrorType
  | DriverErrorType
  | OdspErrorType
  | ContainerLoadErrorType
  | 'unknownError';

export enum OWHErrorType {
  /**
   * The IFrame failed to initialize
   */
  IframeLoadFailed = 'IframeLoadFailed',

  /**
   * Loaded container version is no longer valid and needs upgrade
   *
   * TODO: Consider promoting ContainerUpgradeRequired to FF and expose it via ErrorType
   */
  ContainerUpgradeRequired = 'ContainerUpgradeRequired',

  /**
   * The requested component is not found.
   */
  ComponentNotFound = 'ComponentNotFound'
}

export enum ContainerLoadErrorType {
  /** Root component couldn't have been created or initialized */
  RootComponentCreateFailed = 'RootComponentCreateFailed'
}

export class ErrorBase<T extends string> extends Error {
  constructor(message: string, name: T, readonly internalError?: any, errorStack?: string) {
    super(message);
    this.name = name;

    // Preserve the stack that was passed.
    if (errorStack) {
      try {
        // Not clear if all browsers allow overwriting stack
        this.stack = errorStack;
      } catch {}
    }
  }
}

/**
 * Error class used to communicate that the IFrame service failed to initialize.
 * (Ex: message protocols inside and outside were incompatible)
 */
export class IframeLoadFailedError extends ErrorBase<OWHErrorType> {
  constructor(message: string, internalError?: any) {
    super(message, OWHErrorType.IframeLoadFailed, internalError);
  }
}

/**
 * Error class used to communicate that loaded container version is no longer and needs upgrade
 *
 * TODO: Consider promoting ContainerUpgradeRequired to FF and expose it via ErrorType
 */
export class ContainerUpgradeRequiredError extends ErrorBase<OWHErrorType> {
  constructor(message: string, internalError?: any) {
    super(message, OWHErrorType.ContainerUpgradeRequired, internalError);
  }
}

/**
 * Error class used to communicate that the requested component cannot be found
 * or it has been marked as unreferenced by the Garbage Collection.
 */
export class ComponentNotFoundError extends ErrorBase<OWHErrorType> {
  constructor(message: string, internalError?: any, readonly code?: number, errorStack?: string) {
    super(message, OWHErrorType.ComponentNotFound, internalError, errorStack);
  }
}

/**
 * Error class used to communicate that the root component cannot be created.
 */
export class RootComponentCreateFailedError extends ErrorBase<ContainerLoadErrorType> {
  constructor(message: string, internalError?: any, readonly code?: number, errorStack?: string) {
    super(message, ContainerLoadErrorType.RootComponentCreateFailed, internalError, errorStack);
  }
}
